/**
 * rb-editor.js — the editor column: ONE section at a time.
 *
 * Replaces rb-form.js, which drew all twelve sections as one long list of
 * collapsible cards (the "chaos" the redesign was asked to end): the rail
 * chooses the section, this module draws only it. Text inputs write into the
 * model in place and never rebuild the pane, so focus and the caret survive
 * typing; structure changes (add / remove / move) rebuild the pane.
 *
 * Accessibility the old form lacked (audit 2026-10-09): every input has a
 * <label for>, entries are <details>/<summary>, reorder is buttons (keyboard and
 * touch), icon buttons carry aria-labels.
 *
 * Dates are a month select + a year field instead of <input type=month>: that
 * input is a plain text box in Firefox and desktop Safari, where "03.2021" was
 * stored as typed and printed raw.
 *
 * The assistant (rb-ai) is offered right under the field it improves; its
 * answer is a card the person accepts or ignores — nothing changes by itself —
 * and "As it was" undoes an accepted one.
 *
 * Depends on: RbState, RbI18n, RbAi. globalThis.RbEditor
 */
(function () {
    'use strict';

    var T = function (k, v) { return globalThis.RbI18n.t(k, v); };
    var S = globalThis.RbState;
    var E = function (s) { return globalThis.RbAi.esc(s); };

    var _pane = null, _model = null, _cb = {}, _cur = null, _uid = 0, _open = {};

    function id(p) { return 'rbf' + (++_uid) + (p ? '-' + p : ''); }
    function changed(scope) { if (_cb.onChange) _cb.onChange(scope || 'content'); }
    function structure() { if (_cb.onStructure) _cb.onStructure(); }
    function lang() { return (_model.meta && _model.meta.lang) || globalThis.RbI18n.LOCALE; }
    function region() { return (_model.meta && _model.meta.region) || 'us'; }

    var OPTIONAL = ['certifications', 'projects', 'awards', 'publications', 'volunteer', 'references'];

    /* ---- field specs of list sections ---------------------------------- */
    var SPECS = {
        experience: [
            { f: 'position', label: 'position', w: 2, req: true, ph: 'ph_position' },
            { f: 'company', label: 'company', w: 2, ph: 'ph_company' },
            { f: 'location', label: 'location', w: 2 },
            { f: 'startDate', label: 'start_date', type: 'date', w: 2 },
            { f: 'endDate', label: 'end_date', type: 'date', w: 2, current: true },
            { f: 'description', label: 'description', type: 'area', ai: 'bullets', ph: 'ph_description' },
            { f: 'achievements', label: 'achievements', type: 'area', ai: 'bullets', ph: 'achievements_placeholder' }
        ],
        education: [
            { f: 'institution', label: 'institution', w: 1, req: true },
            { f: 'degree', label: 'degree', w: 2 },
            { f: 'field', label: 'field_of_study', w: 2 },
            { f: 'startDate', label: 'start_date', type: 'date', w: 2 },
            { f: 'endDate', label: 'end_date', type: 'date', w: 2 },
            { f: 'gpa', label: 'gpa', w: 2, opt: true },
            { f: 'honors', label: 'honors', w: 2, opt: true }
        ],
        certifications: [
            { f: 'name', label: 'cert_name', w: 1, req: true },
            { f: 'issuer', label: 'cert_issuer', w: 2 },
            { f: 'date', label: 'cert_date', type: 'date', w: 2 },
            { f: 'url', label: 'cert_url', w: 1, opt: true }
        ],
        projects: [
            { f: 'name', label: 'project_name', w: 2, req: true },
            { f: 'url', label: 'project_url', w: 2, opt: true },
            { f: 'description', label: 'project_description', type: 'area', ai: 'bullets' },
            { f: 'technologies', label: 'project_technologies', w: 1 }
        ],
        awards: [
            { f: 'title', label: 'award_title', w: 2, req: true },
            { f: 'issuer', label: 'award_issuer', w: 2 },
            { f: 'date', label: 'award_date', type: 'date', w: 2 },
            { f: 'description', label: 'award_description', w: 1, opt: true }
        ],
        publications: [
            { f: 'title', label: 'pub_title', w: 1, req: true },
            { f: 'publisher', label: 'pub_publisher', w: 2 },
            { f: 'date', label: 'pub_date', type: 'date', w: 2 },
            { f: 'url', label: 'pub_url', w: 1, opt: true }
        ],
        volunteer: [
            { f: 'role', label: 'vol_role', w: 2, req: true },
            { f: 'organization', label: 'vol_organization', w: 2 },
            { f: 'startDate', label: 'start_date', type: 'date', w: 2 },
            { f: 'endDate', label: 'end_date', type: 'date', w: 2 },
            { f: 'description', label: 'vol_description', type: 'area', ai: 'bullets' }
        ],
        references: [
            { f: 'name', label: 'ref_name', w: 2, req: true },
            { f: 'title', label: 'ref_title', w: 2 },
            { f: 'company', label: 'ref_company', w: 2 },
            { f: 'contact', label: 'ref_contact', w: 2 }
        ]
    };
    var HEAD = { experience: ['position', 'company'], education: ['institution', 'degree'], certifications: ['name', 'issuer'],
        projects: ['name', 'technologies'], awards: ['title', 'issuer'], publications: ['title', 'publisher'],
        volunteer: ['role', 'organization'], references: ['name', 'company'] };

    /* ---- small builders ------------------------------------------------- */

    function lbl(forId, key, opt) {
        return '<label class="rbx-f-l" for="' + forId + '"><span>' + E(T(key)) + '</span>' +
            (opt ? '<span class="rbx-opt">' + E(T('optional')) + '</span>' : '') + '</label>';
    }
    function input(key, value, attrs, o) {
        o = o || {};
        var fid = id(key);
        return '<div class="rbx-f">' + lbl(fid, o.label || key, o.opt) +
            '<input class="rbx-in" id="' + fid + '" ' + attrs + ' value="' + E(value) + '"' +
            (o.ph ? ' placeholder="' + E(T(o.ph)) + '"' : '') + (o.type ? ' type="' + o.type + '"' : '') +
            (o.list ? ' list="' + o.list + '"' : '') + (o.auto ? ' autocomplete="' + o.auto + '"' : '') + '>' +
            (o.hint ? '<p class="rbx-hint">' + E(T(o.hint)) + '</p>' : '') + '</div>';
    }
    function area(key, value, attrs, o) {
        o = o || {};
        var fid = id(key);
        return '<div class="rbx-f" data-area>' + lbl(fid, o.label || key, o.opt) +
            '<textarea class="rbx-ta" id="' + fid + '" ' + attrs + (o.ph ? ' placeholder="' + E(T(o.ph)) + '"' : '') +
            ' rows="' + (o.rows || 4) + '">' + E(value) + '</textarea>' +
            (o.hint ? '<p class="rbx-hint">' + E(T(o.hint)) + '</p>' : '') +
            (o.ai && globalThis.RbAi.live() ? '<div class="rbx-ai-row"><button type="button" class="rbx-ai-btn" data-ai="' + o.ai + '">✨ ' + E(T(o.aiLabel || 'ai_improve')) + '</button></div><div data-ai-host></div>' : '') +
            '</div>';
    }

    var MONTHS = null;
    function monthNames() {
        if (MONTHS) return MONTHS;
        MONTHS = [];
        var loc = (typeof window !== 'undefined' && window.__intlLocale) ? window.__intlLocale() : lang();
        for (var i = 0; i < 12; i++) {
            var n;
            try { n = new Date(2020, i, 1).toLocaleDateString(loc, { month: 'long' }); } catch (e) { n = String(i + 1); }
            MONTHS.push(n.charAt(0).toUpperCase() + n.slice(1));
        }
        return MONTHS;
    }
    /* stored as "YYYY-MM" or "YYYY"; shown as month select + year field */
    function dateField(key, value, attrs, o) {
        o = o || {};
        var m = String(value || '').match(/^(\d{4})(?:-(\d{2}))?/);
        var y = m ? m[1] : '', mo = m && m[2] ? +m[2] : 0;
        var fid = id(key);
        var opts = '<option value="0">' + E(T('month_none')) + '</option>' + monthNames().map(function (n, i) {
            return '<option value="' + (i + 1) + '"' + (mo === i + 1 ? ' selected' : '') + '>' + E(n) + '</option>';
        }).join('');
        return '<div class="rbx-f" data-date ' + attrs + '>' + lbl(fid, o.label || key) +
            '<div style="display:grid;grid-template-columns:minmax(0,1.4fr) minmax(64px,1fr);gap:6px">' +
            '<select class="rbx-sel" id="' + fid + '" data-part="m" aria-label="' + E(T('month')) + '">' + opts + '</select>' +
            '<input class="rbx-in" data-part="y" inputmode="numeric" maxlength="4" placeholder="' + E(T('year')) + '" aria-label="' + E(T('year')) + '" value="' + E(y) + '">' +
            '</div>' + (o.extra || '') + '</div>';
    }
    function readDate(wrap) {
        var y = (wrap.querySelector('[data-part=y]').value || '').replace(/\D/g, '').slice(0, 4);
        var mo = +wrap.querySelector('[data-part=m]').value || 0;
        if (y.length !== 4) return '';
        return mo ? y + '-' + ('0' + mo).slice(-2) : y;
    }

    function paneHead(titleKey, subKey, extra) {
        return '<div class="rbx-pane-h"><h3 class="rbx-pane-t">' + E(typeof titleKey === 'function' ? titleKey() : T(titleKey)) + '</h3>' + (extra || '') + '</div>' +
            (subKey ? '<p class="rbx-pane-sub">' + E(T(subKey)) + '</p>' : '');
    }
    function foot() {
        var order = _cb.order ? _cb.order() : [];
        var i = order.indexOf(_cur);
        var prev = i > 0 ? order[i - 1] : null, next = i > -1 && i < order.length - 1 ? order[i + 1] : null;
        return '<div class="rbx-pane-foot">' +
            (prev ? '<button type="button" class="tc-btn ghost sm" data-goto="' + prev + '">‹ ' + E(_cb.label(prev)) + '</button>' : '<span></span>') +
            (next ? '<button type="button" class="tc-btn sm" data-goto="' + next + '">' + E(_cb.label(next)) + ' ›</button>' : '<button type="button" class="tc-btn sm" data-goto="check">' + E(T('nav_check')) + ' ›</button>') +
            '</div>';
    }

    /* ---- panes ---------------------------------------------------------- */

    function personalPane() {
        var p = _model.personal, c = _model.cultural, pr = _model.prefs || {};
        var h = paneHead('nav_personal', 'pane_personal_sub');
        if (c.photo) {
            h += '<div class="rbx-photo"><button type="button" class="rbx-photo-th" data-photo="pick" aria-label="' + E(T('upload_photo')) + '">' +
                (p.photo ? '<img src="' + E(p.photo) + '" alt="">' : '<span aria-hidden="true">＋</span>') + '</button>' +
                '<div style="display:flex;flex-direction:column;gap:6px"><button type="button" class="tc-btn ghost sm" data-photo="pick">' + E(T(p.photo ? 'change_photo' : 'upload_photo')) + '</button>' +
                (p.photo ? '<button type="button" class="tc-btn ghost sm" data-photo="remove">' + E(T('remove_photo')) + '</button>' : '') +
                '<p class="rbx-hint">' + E(T('photo_hint')) + '</p></div></div>';
        }
        h += '<div class="rbx-2">' + input('full_name', p.name, 'data-p="name"', { auto: 'name' }) +
            input('job_title', p.title, 'data-p="title"', { ph: 'ph_title', hint: 'hint_title' }) + '</div>';
        h += '<div class="rbx-2">' + input('email', p.email, 'data-p="email"', { type: 'email', auto: 'email' }) +
            input('phone', p.phone, 'data-p="phone"', { type: 'tel', auto: 'tel' }) + '</div>';
        h += input('city', p.city, 'data-p="city"', { auto: 'address-level2' });

        var gated = '';
        if (c.dob) gated += input('date_of_birth', p.dob, 'data-p="dob"', { type: 'date' });
        if (c.marital) gated += selectField('marital_status', p.marital, 'data-p="marital"', ['', 'single', 'married', 'divorced', 'widowed'], 'marital_');
        if (c.nationality) gated += input('nationality', p.nationality, 'data-p="nationality"');
        if (c.religion) gated += input('religion', p.religion, 'data-p="religion"');
        if (c.gender) gated += selectField('gender', p.gender, 'data-p="gender"', ['', 'male', 'female', 'other'], 'gender_');
        if (gated) h += '<div class="rbx-2">' + gated + '</div>';

        /* what the person wants — hh.ru's fields; open by default for Russia */
        var prefCount = S.PREF_KEYS.filter(function (k) { return String(pr[k] || '').trim(); }).length;
        h += '<details class="rbx-det"' + (region() === 'ru' || prefCount ? ' open' : '') + '><summary>' + E(T('prefs_title')) +
            '<span class="rbx-det-sum">' + (prefCount ? E(T('n_filled', { n: prefCount })) : E(T('optional'))) + '</span></summary><div class="rbx-det-body">' +
            '<p class="rbx-hint" style="margin-bottom:10px">' + E(T('prefs_hint')) + '</p>' +
            '<div class="rbx-2">' + input('pref_salary', pr.salary, 'data-pref="salary"', { ph: 'ph_salary' }) +
            input('pref_employment', pr.employment, 'data-pref="employment"', { list: 'rbDlEmployment' }) + '</div>' +
            '<div class="rbx-2">' + input('pref_schedule', pr.schedule, 'data-pref="schedule"', { list: 'rbDlSchedule' }) +
            input('pref_relocation', pr.relocation, 'data-pref="relocation"', { list: 'rbDlRelocation' }) + '</div>' +
            '<div class="rbx-2">' + input('pref_travel', pr.travel, 'data-pref="travel"', { list: 'rbDlTravel' }) +
            input('pref_license', pr.license, 'data-pref="license"', { ph: 'ph_license' }) + '</div>' +
            input('pref_citizenship', pr.citizenship, 'data-pref="citizenship"') +
            datalist('rbDlEmployment', 'opt_employment') + datalist('rbDlSchedule', 'opt_schedule') +
            datalist('rbDlRelocation', 'opt_relocation') + datalist('rbDlTravel', 'opt_travel') +
            '</div></details>';

        var qr = _model.qr || {};
        h += '<details class="rbx-det"' + (p.website || p.linkedin || p.github || p.address || qr.include ? ' open' : '') + '><summary>' + E(T('links_title')) +
            '<span class="rbx-det-sum">' + E(T('optional')) + '</span></summary><div class="rbx-det-body">' +
            '<div class="rbx-2">' + input('linkedin', p.linkedin, 'data-p="linkedin"', { ph: 'ph_linkedin' }) + input('github', p.github, 'data-p="github"') + '</div>' +
            '<div class="rbx-2">' + input('website', p.website, 'data-p="website"', { type: 'url' }) + input('address', p.address, 'data-p="address"', { auto: 'street-address' }) + '</div>' +
            '<label class="rbx-check"><input type="checkbox" data-qr="include"' + (qr.include ? ' checked' : '') + '> ' + E(T('qr_include')) + '</label>' +
            '<div data-qr-row' + (qr.include ? '' : ' hidden') + '>' + input('qr_url', qr.url, 'data-qr="url"', { type: 'url', ph: 'ph_url' }) + '</div>' +
            '</div></details>';
        return h + foot();
    }
    function datalist(dlId, key) {
        var items = String(T(key)).split('|').filter(Boolean);
        if (items.length < 2) return '';
        return '<datalist id="' + dlId + '">' + items.map(function (x) { return '<option value="' + E(x) + '">'; }).join('') + '</datalist>';
    }
    function selectField(key, value, attrs, opts, prefix) {
        var fid = id(key);
        return '<div class="rbx-f">' + lbl(fid, key) + '<select class="rbx-sel" id="' + fid + '" ' + attrs + '>' +
            opts.map(function (o) { return '<option value="' + o + '"' + (value === o ? ' selected' : '') + '>' + E(o ? T(prefix + o) : '—') + '</option>'; }).join('') +
            '</select></div>';
    }

    function summaryPane() {
        var h = paneHead('section_summary', 'pane_summary_sub');
        h += area('section_summary', _model.summary, 'data-summary', { rows: 5, ph: 'summary_placeholder', ai: 'summary', aiLabel: _model.summary ? 'ai_summary_rewrite' : 'ai_summary_write' });
        h += '<p class="rbx-hint" data-counter></p>';
        h += '<details class="rbx-det"><summary>' + E(T('summary_help_t')) + '</summary><div class="rbx-det-body"><ul class="rbx-sugg-list">' +
            String(T('summary_help')).split('|').map(function (x) { return '<li>' + E(x) + '</li>'; }).join('') + '</ul></div></details>';
        return h + foot();
    }

    function entrySummary(sec, e) {
        var keys = HEAD[sec] || [];
        var a = String(e[keys[0]] || '').trim(), b = String(e[keys[1]] || '').trim();
        var dates = '';
        if (e.startDate || e.endDate || e.current) dates = globalThis.RbI18n.dateRange(e.startDate, e.endDate, e.current, 'MM.YYYY', T('present'));
        return '<span class="rbx-entry-t"><b>' + E(a || T('untitled_entry')) + '</b><span>' + E([b, dates].filter(Boolean).join(' · ') || ' ') + '</span></span>';
    }

    function listPane(sec) {
        var items = _model[sec] || [];
        var removable = OPTIONAL.indexOf(sec) > -1;
        var h = paneHead('section_' + sec, 'pane_' + sec + '_sub',
            removable ? '<button type="button" class="tc-btn ghost sm" data-drop-sec style="margin-inline-start:auto">' + E(T('remove_section')) + '</button>' : '');
        if (!items.length) h += '<p class="rbx-hint" style="margin-bottom:10px">' + E(T('empty_' + sec)) + '</p>';
        items.forEach(function (e, i) {
            var open = _open[sec] === e.id || (i === 0 && _open[sec] === undefined);
            h += '<details class="rbx-entry" data-idx="' + i + '"' + (open ? ' open' : '') + '><summary>' + entrySummary(sec, e) +
                '<button type="button" class="rbx-ib rbx-ea" data-act="up" aria-label="' + E(T('move_up')) + '"' + (i === 0 ? ' disabled' : '') + '>↑</button>' +
                '<button type="button" class="rbx-ib rbx-ea" data-act="down" aria-label="' + E(T('move_down')) + '"' + (i === items.length - 1 ? ' disabled' : '') + '>↓</button>' +
                '<button type="button" class="rbx-ib rbx-ea" data-act="dup" aria-label="' + E(T('duplicate')) + '">⧉</button>' +
                '<button type="button" class="rbx-ib danger" data-act="del" aria-label="' + E(T('remove')) + '">✕</button>' +
                '</summary><div class="rbx-entry-body">' +
                /* narrow screens: the same moves as labelled buttons, so the summary keeps room for the title */
                '<div class="rbx-entry-acts">' +
                (i > 0 ? '<button type="button" class="tc-btn ghost sm" data-act="up">↑ ' + E(T('move_up')) + '</button>' : '') +
                (i < items.length - 1 ? '<button type="button" class="tc-btn ghost sm" data-act="down">↓ ' + E(T('move_down')) + '</button>' : '') +
                '<button type="button" class="tc-btn ghost sm" data-act="dup">⧉ ' + E(T('duplicate')) + '</button></div>' +
                entryFields(sec, e) + '</div></details>';
        });
        h += '<button type="button" class="rbx-add" data-add-entry>＋ ' + E(T('add_' + sec.replace(/ies$/, 'y').replace(/s$/, ''))) + '</button>';
        if (sec === 'experience') h += '<p class="rbx-hint" style="margin-top:10px">' + E(T('pane_experience_tip')) + '</p>';
        return h + foot();
    }

    function entryFields(sec, e) {
        var specs = SPECS[sec];
        var out = '', row = [];
        function flush() { if (row.length) { out += row.length > 1 ? '<div class="rbx-2">' + row.join('') + '</div>' : row[0]; row = []; } }
        specs.forEach(function (sp) {
            var attrs = 'data-f="' + sp.f + '"';
            var html;
            if (sp.type === 'date') {
                var extra = '';
                if (sp.current) extra = '<label class="rbx-check"><input type="checkbox" data-f="current"' + (e.current ? ' checked' : '') + '> ' + E(T('present')) + '</label>';
                if (sp.current && e.current) {
                    html = '<div class="rbx-f">' + '<span class="rbx-f-l">' + E(T(sp.label)) + '</span>' +
                        '<div class="rbx-in" style="display:flex;align-items:center;color:var(--lg-text-2)">' + E(T('present')) + '</div>' + extra + '</div>';
                } else {
                    html = dateField(sp.label, e[sp.f], attrs, { label: sp.label, extra: extra });
                }
            } else if (sp.type === 'area') {
                flush();
                out += area(sp.label, e[sp.f], attrs, { ph: sp.ph, ai: sp.ai, rows: 4 });
                return;
            } else {
                html = input(sp.label, e[sp.f], attrs, { opt: sp.opt, ph: sp.ph });
            }
            if (sp.w === 1) { flush(); out += html; return; }
            row.push(html);
            if (row.length === 2) flush();
        });
        flush();
        return out;
    }

    function skillsPane() {
        var sk = _model.skills || [];
        var h = paneHead('section_skills', 'pane_skills_sub');
        var fid = id('skill');
        h += '<div class="rbx-f"><label class="rbx-f-l" for="' + fid + '">' + E(T('skill_add_label')) + '</label>' +
            '<input class="rbx-in" id="' + fid + '" data-skill-input placeholder="' + E(T('skill_add_ph')) + '" enterkeyhint="done"></div>';
        h += '<div class="rbx-tags" role="list">' + sk.map(function (k, i) {
            return '<span class="rbx-tag" role="listitem"><span>' + E(k.name) + '</span><button type="button" data-skill-del="' + i + '" aria-label="' + E(T('remove') + ': ' + k.name) + '">✕</button></span>';
        }).join('') + '</div>';
        if (!sk.length) h += '<p class="rbx-hint">' + E(T('empty_skills')) + '</p>';
        var tpl = globalThis.RbTemplates.get(_model.meta.template);
        if (sk.length && tpl.skillStyle === 'bar') {
            h += '<details class="rbx-det"><summary>' + E(T('skill_levels')) + '</summary><div class="rbx-det-body">' +
                sk.map(function (k, i) {
                    return selectField('skill_level', k.level || 'intermediate', 'data-skill-lvl="' + i + '" aria-label="' + E(k.name) + '"',
                        ['beginner', 'elementary', 'intermediate', 'advanced', 'expert'], 'level_').replace(E(T('skill_level')), E(k.name));
                }).join('') + '</div></details>';
        }
        return h + foot();
    }

    var LANG_LIST = ['ru', 'en', 'de', 'fr', 'es', 'it', 'pt', 'zh', 'ja', 'ko', 'ar', 'tr', 'uk', 'kk', 'pl', 'nl', 'uz', 'tt', 'be', 'id'];
    function languagesPane() {
        var ls = _model.languages || [];
        var h = paneHead('section_languages', 'pane_languages_sub');
        var names = '';
        try {
            var dn = new Intl.DisplayNames([lang()], { type: 'language' });
            names = '<datalist id="rbDlLangs">' + LANG_LIST.map(function (c) {
                var n = dn.of(c) || c; return '<option value="' + E(n.charAt(0).toUpperCase() + n.slice(1)) + '">';
            }).join('') + '</datalist>';
        } catch (e) {}
        ls.forEach(function (l, i) {
            h += '<div class="rbx-2" data-lang-row="' + i + '" style="align-items:end">' +
                input('language_name', l.name, 'data-lang-f="name"', { list: 'rbDlLangs' }) +
                '<div style="display:flex;gap:6px;align-items:flex-end">' +
                selectField('proficiency', l.proficiency || 'b1', 'data-lang-f="proficiency"', ['a1', 'a2', 'b1', 'b2', 'c1', 'c2', 'native'], 'prof_').replace('class="rbx-f"', 'class="rbx-f" style="flex:1 1 auto"') +
                '<button type="button" class="rbx-ib danger" data-lang-del="' + i + '" aria-label="' + E(T('remove')) + '" style="margin-bottom:16px">✕</button></div></div>';
        });
        h += names + '<button type="button" class="rbx-add" data-add-lang>＋ ' + E(T('add_language')) + '</button>';
        return h + foot();
    }

    function customPane(ci) {
        var cs = (_model.custom || [])[ci];
        if (!cs) return '';
        var h = paneHead(function () { return cs.title || T('section_custom'); }, 'pane_custom_sub',
            '<button type="button" class="tc-btn ghost sm" data-drop-custom style="margin-inline-start:auto">' + E(T('remove_section')) + '</button>');
        h += input('custom_title', cs.title, 'data-ctitle', { ph: 'ph_custom_title' });
        h += area('custom_items', (cs.items || []).join('\n'), 'data-citems', { rows: 6, hint: 'custom_items_hint' });
        return h + foot();
    }

    /* ---- render + bind -------------------------------------------------- */

    function show(paneId, o) {
        if (!_pane) return;
        o = o || {};
        _cur = paneId;
        var html;
        if (paneId === 'personal') html = personalPane();
        else if (paneId === 'summary') html = summaryPane();
        else if (paneId === 'skills') html = skillsPane();
        else if (paneId === 'languages') html = languagesPane();
        else if (/^custom:\d+$/.test(paneId)) html = customPane(+paneId.split(':')[1]);
        else if (SPECS[paneId]) html = listPane(paneId);
        else return false;
        _pane.innerHTML = html;
        bind(paneId);
        if (!o.keepScroll) _pane.scrollTop = 0;
        if (paneId === 'summary') counter();
        return true;
    }

    function counter() {
        var c = _pane.querySelector('[data-counter]');
        if (c) c.textContent = T('chars_count', { n: String(_model.summary || '').length });
    }

    function bind(paneId) {
        var root = _pane;
        root.querySelectorAll('[data-goto]').forEach(function (b) {
            b.addEventListener('click', function () { if (_cb.go) _cb.go(b.dataset.goto); });
        });
        root.querySelectorAll('[data-p]').forEach(function (inp) {
            inp.addEventListener(inp.tagName === 'SELECT' ? 'change' : 'input', function () {
                _model.personal[inp.dataset.p] = inp.value;
                if (_model.meta.sample && (inp.dataset.p === 'name')) _model.meta.sample = '';
                changed();
            });
        });
        root.querySelectorAll('[data-pref]').forEach(function (inp) {
            inp.addEventListener('input', function () { _model.prefs = _model.prefs || {}; _model.prefs[inp.dataset.pref] = inp.value; changed(); });
        });
        root.querySelectorAll('[data-qr]').forEach(function (inp) {
            if (!_model.qr) _model.qr = { url: '', include: false, dataUrl: '' };
            if (inp.dataset.qr === 'include') {
                inp.addEventListener('change', function () {
                    _model.qr.include = inp.checked;
                    var row = root.querySelector('[data-qr-row]');
                    if (row) row.hidden = !inp.checked;
                    changed();
                });
            } else inp.addEventListener('input', function () { _model.qr.url = inp.value.trim(); changed(); });
        });
        root.querySelectorAll('[data-photo]').forEach(function (b) {
            b.addEventListener('click', function () {
                if (b.dataset.photo === 'remove') { _model.personal.photo = ''; show(paneId, { keepScroll: true }); changed(); return; }
                pickPhoto(paneId);
            });
        });
        var sum = root.querySelector('[data-summary]');
        if (sum) sum.addEventListener('input', function () { _model.summary = sum.value; counter(); changed(); });

        /* list sections */
        if (SPECS[paneId]) bindList(paneId, root);
        if (paneId === 'skills') bindSkills(root);
        if (paneId === 'languages') bindLanguages(root);
        if (/^custom:/.test(paneId)) bindCustom(+paneId.split(':')[1], root);

        var drop = root.querySelector('[data-drop-sec]');
        if (drop) drop.addEventListener('click', function () {
            var go = function () {
                _model[paneId] = [];
                if (_cb.dropSection) _cb.dropSection(paneId);
                structure();
            };
            if ((_model[paneId] || []).length && _cb.confirm) _cb.confirm(T('remove_section_q'), T('remove_section_d'), T('remove'), go); else go();
        });

        /* the assistant under its field */
        root.querySelectorAll('[data-ai]').forEach(function (b) {
            b.addEventListener('click', function () { aiFor(b, paneId); });
        });
    }

    function bindList(sec, root) {
        root.querySelectorAll('.rbx-entry').forEach(function (det) {
            var i = +det.dataset.idx;
            var e = _model[sec][i];
            det.addEventListener('toggle', function () {
                if (det.open) {
                    _open[sec] = e.id;
                    root.querySelectorAll('.rbx-entry[open]').forEach(function (o) { if (o !== det) o.open = false; });
                }
            });
            det.querySelectorAll('[data-f]').forEach(function (inp) {
                var key = inp.dataset.f;
                if (inp.matches('[data-date]')) return;   /* the month+year pair is bound below */
                if (inp.type === 'checkbox') {
                    inp.addEventListener('change', function () {
                        e.current = inp.checked;
                        if (inp.checked) e.endDate = '';
                        _open[sec] = e.id;
                        show(sec, { keepScroll: true });
                        changed();
                    });
                    return;
                }
                inp.addEventListener('input', function () {
                    e[key] = inp.value;
                    if (HEAD[sec] && HEAD[sec].indexOf(key) > -1) {
                        var t = det.querySelector('summary .rbx-entry-t');
                        if (t) t.outerHTML = entrySummary(sec, e);
                    }
                    changed();
                });
            });
            det.querySelectorAll('[data-date]').forEach(function (wrap) {
                var key = wrap.dataset.f;
                var upd = function () {
                    var v = readDate(wrap);
                    if (v || !wrap.querySelector('[data-part=y]').value) { e[key] = v; changed(); }
                };
                wrap.querySelector('[data-part=m]').addEventListener('change', upd);
                wrap.querySelector('[data-part=y]').addEventListener('input', upd);
            });
            det.querySelectorAll('[data-act]').forEach(function (b) {
                b.addEventListener('click', function (ev) {
                    ev.preventDefault(); ev.stopPropagation();
                    var arr = _model[sec], act = b.dataset.act;
                    var run = function () {
                        if (act === 'del') arr.splice(i, 1);
                        else if (act === 'dup') { var cp = Object.assign(S.newEntry(sec), S.clone(arr[i]), { id: S.uid() }); arr.splice(i + 1, 0, cp); _open[sec] = cp.id; }
                        else if (act === 'up' && i > 0) arr.splice(i - 1, 0, arr.splice(i, 1)[0]);
                        else if (act === 'down' && i < arr.length - 1) arr.splice(i + 1, 0, arr.splice(i, 1)[0]);
                        show(sec, { keepScroll: true });
                        structure();
                        if (act === 'up' || act === 'down') {
                            var moved = _pane.querySelector('.rbx-entry[data-idx="' + (act === 'up' ? i - 1 : i + 1) + '"] [data-act="' + act + '"]');
                            if (moved && !moved.disabled) moved.focus(); else { var any = _pane.querySelector('.rbx-entry[data-idx="' + (act === 'up' ? i - 1 : i + 1) + '"] summary'); if (any) any.focus(); }
                        }
                    };
                    var filled = Object.keys(arr[i]).some(function (k) { return k !== 'id' && k !== 'current' && String(arr[i][k] || '').trim(); });
                    if (act === 'del' && filled && _cb.confirm) _cb.confirm(T('remove_entry_q'), entryLabel(sec, arr[i]), T('remove'), run); else run();
                });
            });
        });
        var add = root.querySelector('[data-add-entry]');
        if (add) add.addEventListener('click', function () {
            var ne = S.newEntry(sec);
            _model[sec].push(ne);
            _open[sec] = ne.id;
            show(sec, { keepScroll: true });
            structure();
            var last = _pane.querySelector('.rbx-entry[open] input, .rbx-entry[open] textarea');
            if (last) last.focus();
        });
    }
    function entryLabel(sec, e) {
        var keys = HEAD[sec] || [];
        return [e[keys[0]], e[keys[1]]].filter(function (x) { return String(x || '').trim(); }).join(' — ');
    }

    function bindSkills(root) {
        var inp = root.querySelector('[data-skill-input]');
        var add = function () {
            var parts = inp.value.split(/[,;\n]/).map(function (x) { return x.trim(); }).filter(Boolean);
            if (!parts.length) return;
            var have = (_model.skills || []).map(function (k) { return k.name.toLowerCase(); });
            parts.forEach(function (n) {
                if (have.indexOf(n.toLowerCase()) < 0) { _model.skills.push(Object.assign(S.newEntry('skills'), { name: n.slice(0, 80) })); have.push(n.toLowerCase()); }
            });
            show('skills', { keepScroll: true });
            structure();
            var again = _pane.querySelector('[data-skill-input]');
            if (again) again.focus();
        };
        if (inp) {
            inp.addEventListener('keydown', function (e) {
                if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); add(); }
                else if (e.key === 'Backspace' && !inp.value && _model.skills.length) { _model.skills.pop(); show('skills', { keepScroll: true }); structure(); var a2 = _pane.querySelector('[data-skill-input]'); if (a2) a2.focus(); }
            });
            inp.addEventListener('blur', function () { if (inp.value.trim()) add(); });
            inp.addEventListener('paste', function () { setTimeout(function () { if (/[,;\n]/.test(inp.value)) add(); }, 0); });
        }
        root.querySelectorAll('[data-skill-del]').forEach(function (b) {
            b.addEventListener('click', function () { _model.skills.splice(+b.dataset.skillDel, 1); show('skills', { keepScroll: true }); structure(); });
        });
        root.querySelectorAll('[data-skill-lvl]').forEach(function (sel) {
            sel.addEventListener('change', function () { _model.skills[+sel.dataset.skillLvl].level = sel.value; changed(); });
        });
    }

    function bindLanguages(root) {
        root.querySelectorAll('[data-lang-row]').forEach(function (row) {
            var l = _model.languages[+row.dataset.langRow];
            row.querySelectorAll('[data-lang-f]').forEach(function (inp) {
                inp.addEventListener(inp.tagName === 'SELECT' ? 'change' : 'input', function () { l[inp.dataset.langF] = inp.value; changed(); });
            });
        });
        root.querySelectorAll('[data-lang-del]').forEach(function (b) {
            b.addEventListener('click', function () { _model.languages.splice(+b.dataset.langDel, 1); show('languages', { keepScroll: true }); structure(); });
        });
        var add = root.querySelector('[data-add-lang]');
        if (add) add.addEventListener('click', function () {
            _model.languages.push(S.newEntry('languages'));
            show('languages', { keepScroll: true }); structure();
            var ins = _pane.querySelectorAll('[data-lang-f="name"]');
            if (ins.length) ins[ins.length - 1].focus();
        });
    }

    function bindCustom(ci, root) {
        var cs = _model.custom[ci];
        var t = root.querySelector('[data-ctitle]');
        if (t) t.addEventListener('input', function () {
            cs.title = t.value;
            var h = root.querySelector('.rbx-pane-t');
            if (h) h.textContent = t.value || T('section_custom');
            if (_cb.relabel) _cb.relabel();
            changed();
        });
        var it = root.querySelector('[data-citems]');
        if (it) it.addEventListener('input', function () { cs.items = it.value.split('\n'); changed(); });
        var del = root.querySelector('[data-drop-custom]');
        if (del) del.addEventListener('click', function () {
            var go = function () { _model.custom.splice(ci, 1); if (_cb.dropSection) _cb.dropSection('custom:' + ci); structure(); };
            if ((cs.items || []).some(function (x) { return String(x).trim(); }) && _cb.confirm) _cb.confirm(T('remove_section_q'), cs.title || '', T('remove'), go); else go();
        });
    }

    function pickPhoto(paneId) {
        var inp = document.createElement('input');
        inp.type = 'file';
        inp.accept = 'image/*';
        inp.addEventListener('change', function () {
            var f = inp.files && inp.files[0];
            if (!f) return;
            globalThis.RbPhoto.openFromFile(f, function (dataUrl) {
                _model.personal.photo = dataUrl;
                show(paneId, { keepScroll: true });
                changed();
            });
        });
        inp.click();
    }

    /* ---- assistant under a field ---------------------------------------- */

    var _busy = null;
    function aiFor(btn, paneId) {
        var wrap = btn.closest('[data-area]');
        var ta = wrap && wrap.querySelector('textarea');
        var host = wrap && wrap.querySelector('[data-ai-host]');
        if (!ta || !host) return;
        if (_busy) { _busy.abort(); _busy = null; }
        host.innerHTML = '';
        var task = btn.dataset.ai;
        var body = { lang: lang(), format: region() };
        var entry = null;
        if (task === 'bullets') {
            var text = ta.value.trim();
            if (text.length < 12) { if (window.__toast) window.__toast(T('ai_need_text'), 'warning'); ta.focus(); return; }
            var det = btn.closest('.rbx-entry');
            entry = det ? _model[paneId][+det.dataset.idx] : null;
            body.text = text;
            body.position = entry ? (entry.position || entry.role || entry.name || '') : '';
            body.company = entry ? (entry.company || entry.organization || '') : '';
            body.current = !!(entry && entry.current);
        } else if (task === 'summary') {
            if (S.isEmpty(_model) || !((_model.experience || []).length || (_model.education || []).length || (_model.skills || []).length)) {
                if (window.__toast) window.__toast(T('ai_need_resume'), 'warning'); return;
            }
            body.resume = _cb.payloadModel ? _cb.payloadModel() : _model;
            body.target = _model.personal.title || '';
        }
        var ctl = new AbortController();
        _busy = ctl;
        btn.disabled = true;
        var w = globalThis.RbAi.waitBlock(host, T('ai_working_' + task));
        w.stopBtn.addEventListener('click', function () { ctl.abort(); });
        globalThis.RbAi.run(task, body, { signal: ctl.signal, onWait: w.set }).then(function (res) {
            w.remove();
            btn.disabled = false;
            _busy = null;
            suggestion(host, task, res, ta);
        }, function (e) {
            w.remove();
            btn.disabled = false;
            _busy = null;
            if (e && e.kind === 'cancel') return;
            globalThis.RbAi.errorBlock(host, e || {}, function () { host.innerHTML = ''; btn.click(); });
        });
    }

    var STYLES = { bullets: ['impact', 'concise', 'complete'], summary: ['standard', 'short'] };
    function suggestion(host, task, res, ta) {
        var versions = (res && res.versions) || [];
        if (!versions.length) { globalThis.RbAi.errorBlock(host, { message: T('ai_err_broken') }); return; }
        var order = STYLES[task] || [];
        versions.sort(function (a, b) { return order.indexOf(a.style) - order.indexOf(b.style); });
        var cur = 0;
        var box = document.createElement('div');
        box.className = 'rbx-sugg';
        var textOf = function (v) { return task === 'bullets' ? v.bullets.join('\n') : v.text; };
        function draw() {
            var v = versions[cur];
            var body = task === 'bullets'
                ? '<ul class="rbx-sugg-list">' + v.bullets.map(function (b) { return '<li>' + globalThis.RbAi.marked(b) + '</li>'; }).join('') + '</ul>'
                : '<p class="rbx-sugg-text">' + globalThis.RbAi.marked(v.text) + '</p>';
            var hasPh = /\[[^\]\n]{1,80}\]/.test(textOf(v));
            box.innerHTML = '<div class="rbx-sugg-h">✨ ' + E(T('ai_suggest_h')) + '</div>' +
                (versions.length > 1 ? '<div class="rbx-sugg-tabs" role="tablist">' + versions.map(function (x, i) {
                    return '<button type="button" class="rbx-sugg-tab" role="tab" aria-selected="' + (i === cur) + '" data-v="' + i + '">' + E(T('style_' + x.style)) + '</button>';
                }).join('') + '</div>' : '') + body +
                (hasPh ? '<p class="rbx-hint" style="margin-top:6px">' + E(T('ai_placeholders_hint')) + '</p>' : '') +
                (res.questions && res.questions.length ? '<div class="rbx-sugg-q">' + E(T('ai_questions')) + '<ul>' + res.questions.map(function (q) { return '<li>' + E(q) + '</li>'; }).join('') + '</ul></div>' : '') +
                '<div class="rbx-sugg-act"><button type="button" class="tc-btn sm" data-apply>' + E(T('ai_apply')) + '</button>' +
                (ta.value.trim() && task === 'summary' ? '' : '') +
                '<button type="button" class="tc-btn ghost sm" data-dismiss>' + E(T('ai_dismiss')) + '</button></div>' +
                '<p class="rbx-disclose">' + globalThis.RbAi.disclosure() + '</p>';
            box.querySelectorAll('[data-v]').forEach(function (b) { b.addEventListener('click', function () { cur = +b.dataset.v; draw(); }); });
            box.querySelector('[data-apply]').addEventListener('click', function () {
                var before = ta.value;
                ta.value = textOf(versions[cur]);
                ta.dispatchEvent(new Event('input', { bubbles: true }));
                if (_cb.track) _cb.track('ai_apply', { task: task, style: versions[cur].style });
                box.innerHTML = '<div class="rbx-sugg-act" style="margin:0;align-items:center"><span class="rbx-hint" style="flex:1 1 auto">' + E(T('ai_applied')) + '</span>' +
                    '<button type="button" class="tc-btn ghost sm" data-undo>' + E(T('ai_undo')) + '</button></div>';
                box.querySelector('[data-undo]').addEventListener('click', function () {
                    ta.value = before;
                    ta.dispatchEvent(new Event('input', { bubbles: true }));
                    box.remove();
                    if (_cb.track) _cb.track('ai_undo', { task: task });
                });
            });
            box.querySelector('[data-dismiss]').addEventListener('click', function () { box.remove(); });
        }
        draw();
        host.appendChild(box);
        if (box.scrollIntoView) box.scrollIntoView({ block: 'nearest' });
    }

    /* ---- public ---------------------------------------------------------- */

    function mount(paneEl, model, callbacks) {
        _pane = paneEl;
        _model = model;
        _cb = callbacks || {};
    }
    function setModel(model) { _model = model; _open = {}; }
    function current() { return _cur; }
    function rerender() { if (_cur) show(_cur, { keepScroll: true }); }

    /* Open a pane and put the caret where a check points (entry idx + field). */
    function focusField(paneId, o) {
        o = o || {};
        if (o.idx != null && _model[paneId] && _model[paneId][o.idx]) _open[paneId] = _model[paneId][o.idx].id;
        if (_cur !== paneId) show(paneId); else show(paneId, { keepScroll: true });
        var scope = _pane;
        if (o.idx != null) scope = _pane.querySelector('.rbx-entry[data-idx="' + o.idx + '"]') || _pane;
        var el = null;
        if (o.field) el = scope.querySelector('[data-p="' + o.field + '"], [data-f="' + o.field + '"], [data-' + o.field + ']');
        if (el && el.matches('[data-date]')) el = el.querySelector('select');
        if (!el) el = scope.querySelector('input, textarea, select');
        if (el) {
            if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
            try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
        }
    }

    globalThis.RbEditor = {
        SPECS: SPECS, OPTIONAL: OPTIONAL,
        mount: mount, setModel: setModel, show: show, current: current, rerender: rerender, focusField: focusField
    };
})();
